import Bugsnag from '@bugsnag/js';
import isEmpty from 'lodash/isEmpty';
import { SearchResultListMetaAllOf } from 'swagger/models';

type BoundingBox = {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
};
type Coords = { lat: number; lng: number };
export const getCenter = (
  boundingBox: BoundingBox | null,
  lat?: number,
  lng?: number,
  searchResultsMeta?: SearchResultListMetaAllOf
): Coords => {
  if ((lat || lng) && lat !== 0 && lng !== 0) {
    return {
      lng,
      lat,
    };
  }

  if (searchResultsMeta) {
    if (searchResultsMeta?.geography?.original) {
      // eslint-disable-next-line no-unsafe-optional-chaining
      const { lat: lLat, lng: lLng } = searchResultsMeta?.geography?.original;
      return {
        lat: lLat,
        lng: lLng,
      };
    }
  }

  if (boundingBox) return boxCenter(boundingBox);

  Bugsnag.notify(new Error('getCenter called with no valid arguments'));
  return null;
};

// Normalize [0, 360) to [-180, 180)
const normalize = (lon: number): number => ((((lon + 180) % 360) + 360) % 360) - 180;

/**
 * If east is greater than west, treat it as cartesian.
 *
 * If east is less than west, we must calculate the midpoint between the two longitudes by crossing the antimeridian.
 *
 * This covers the edge case of where one of the two longitudes crosses the antimeridian, the 180/-180 degree longitude
 * boundary in the Pacific Ocean.
 *
 * The most common case where the antimeridian is crossed is in the bounding box returned by the Google Maps API for the
 * United States, as the Aleutian Islands stretch South of Russia.
 *
 * @param west - western boundary of the bounding box, ie. sw_lng. This is assumed to be in the [-180, 180] range.
 * @param east - eastern boundary of the bounding box, ie. ne_lng. This is assumed to be in the [-180, 180] range.
 * @returns -180 ≤ n < 180; the midpoint of the two longitudes
 */
export const getDirectionalLngMidpoint = ({ west, east }: { west: number; east: number }): number => {
  // Directed span size in [0, 360)
  const span = (((east - west) % 360) + 360) % 360;
  // Halfway along that directed span
  const midPoint = normalize(west + span / 2);
  // Always use -180 for the antimeridian in line with the convention of inclusive minimum bounds and exclusive maximum
  // bounds.
  return midPoint === 180 ? -180 : midPoint;
};

export const boxCenter = (box: BoundingBox) => {
  // Given that don't wrap the North or South poles, we can do a simple average of the latitudes.
  const midLat = (box.sw_lat + box.ne_lat) / 2;
  // Given that we can wrap the antimeridian, we need something more complicated to calculate the center of the box.
  const midLng = getDirectionalLngMidpoint({ west: box.sw_lng, east: box.ne_lng });
  return { lat: midLat, lng: midLng };
};

export const tokenizeSearchLocationString = (location: string) => {
  // Here we introduced a try catch block, do we still need the isEmpty? Or in this case always return locationstring
  try {
    const locationString = location.split(',').map((t) => t.toLowerCase().trim());
    return isEmpty(location) ? [] : locationString;
  } catch (err) {
    Bugsnag.notify(err);
    return [];
  }
};

/**
 * This makes an assumption that the last token is the country.
 *
 * This defaults to false, rejecting any location that doesn't match the list of allowed countries below.
 *
 * !!!! This is dangerous if we are sending a location string directly.
 */
export const locationIsDomesticSearch = (tokens: string[]) => {
  if (tokens.length === 0) {
    return true;
  }

  const allowedCountries = ['United States', 'US', 'USA', 'Puerto Rico', 'PR', 'U.S. Virgin Islands', 'USVI'].map(
    (loc) => loc.toLowerCase()
  );
  return allowedCountries.includes(tokens[tokens.length - 1]);
};
